import axios, { AxiosResponse } from "axios";
import { CompanyRegisterModel } from "../../models/dto/auth/companyRegister.dto";
import { CustomerRegisterModel } from "../../models/dto/auth/customerRegister.dto";
import { jwtDecode } from "jwt-decode";
import { UserRole } from "../../models/dto/users/user.dto";
import dataLayerHelper from "../../helpers/dataLayerHelper/dataLayerHelper";
import customerService from "../customers/customer.service";
import configurationService from "../configuration/configuration.service";
import { clearBookabusPriceCheckout } from "../../pages/bookabusPrice/wizard/checkoutStorage";


const API_URL = process.env.REACT_APP_BASE_API_URL || process.env.NEXT_PUBLIC_BASE_API_URL;
const registerCompany = (registerModel: CompanyRegisterModel) => {
    return axios.post(API_URL + "/company/application/register", registerModel)
        .then((response) => {
            return response.data;
        });
};
const registerCustomer = (registerModel: CustomerRegisterModel) => {
    return axios.post(API_URL + "/customer/register", registerModel)
        .then((response) => {
            return response.data;
        });
};
const validateCustomerEmail = (emailAddress: string) => {
    return axios.post(API_URL + "/user/forgot-password-send-email", { email: emailAddress }, { headers: { 'Content-Type': 'application/json' } })
        .then((response) => {
            return response.data;
        });
};
const validateChangePasswordHash = (hash: string) => {
    return axios.post(API_URL + "/user/forgot-password-validate-uuid", { uuid: hash })
        .then((response) => {
            return response.data;
        });
};
const changePassword = (hash: string, newPassword: string) => {
    return axios.post(API_URL + "/user/forgot-password-change-password", { uuid: hash, password: newPassword, passwordRepeat: newPassword })
        .then((response) => {
            return response.data;
        });
};
// `redirectPath` is stored on the link server-side and comes back as a returnUrl on the
// emailed URL, so a customer who asked for a link while trying to reach a guarded page
// (e.g. a purchase's card-setup page) lands there instead of on the booking list. The
// backend sanitises it to a same-site path before it is mailed out.
const getMagicLink = (email: string, redirectPath?: string | null): any => {
    return axios.post<any>(API_URL + "/public/magiclink", { email: email, redirectPath: redirectPath ?? null })
        .then((response) => {
            return response.data;
        });
}
const verifyMagicLink = (id: string): any => {
    return axios.post<any>(API_URL + "/public/magiclink/login", { link: id })
        .then((response) => {
            return response.data;
        });
}
// BOOK-2006: exchange a Google ID token credential for either JWTs (existing user, email
// matched) or a prefill payload (new user, verified email) so the caller can bounce to sign-up.
const loginWithGoogle = (credential: string): Promise<{
    registered: boolean,
    tokens?: { token: string, refreshToken: string },
    prefill?: { email: string, firstName?: string, lastName?: string }
}> => {
    return axios.post(API_URL + "/public/auth/oauth/google/login", { credential })
        .then((response) => response.data);
}

const login = async (
    username: string,
    password: string,
    roleRedirectUrls?: { role: string, url: string }[]
): Promise<string | undefined> => {

    const response = await axios.post(API_URL + "/auth/login", {
        username,
        password,
    });

    if (response.data.token) {
        return await handleTokenLogin(response.data, username, roleRedirectUrls);
    }

    return undefined;
};
const handleTokenLogin = async (
    tokenObject: { token: string, refreshToken: string },
    username: string,
    roleRedirectUrls?: { role: string, url: string }[]
): Promise<string> => {
    dataLayerHelper.push({
        event: "login",
        user_id: username
    });
    localStorage.setItem("token", JSON.stringify(tokenObject));
    let resolvedRole: string;

    if (isTrainReplacementAdmin()) {
        resolvedRole = "TRAIN_REPLACEMENT";
        setRoleAndRedirect(resolvedRole, "CORPORATE", roleRedirectUrls);
    }
    else if (
        isUserInRole(UserRole.DealCustomerAdmin) ||
        isUserInRole(UserRole.DealCustomerBooker) ||
        isUserInRole(UserRole.DealCustomerFinance)
    ) {
        resolvedRole = UserRole.DealCustomerAdmin;
        setRoleAndRedirect(resolvedRole, "CORPORATE", roleRedirectUrls);
    }
    else if (isUserInRole(UserRole.CompanyAdmin)) {
        resolvedRole = UserRole.CompanyAdmin;
        setRoleAndRedirect(resolvedRole, "CORPORATE", roleRedirectUrls);
    }
    else if (isUserInRole(UserRole.TrafficController)) {
        resolvedRole = UserRole.TrafficController;
        setRoleAndRedirect(resolvedRole, "CORPORATE", roleRedirectUrls);
    }
    else if (isUserInRole(UserRole.Driver)) {
        resolvedRole = UserRole.Driver;
        setRoleAndRedirect(resolvedRole, "PRIVATE", roleRedirectUrls);
    }
    else if (authService.isUserInRole("CUSTOMER")) {
        const res = await customerService.getContactInfo();
        resolvedRole = UserRole.Customer;
        setRoleAndRedirect(resolvedRole, res.contactInfoType, roleRedirectUrls);
    }
    else {
        resolvedRole = getCurrentUserRole();
        setRoleAndRedirect(resolvedRole, "PRIVATE", roleRedirectUrls);
    }
    return resolvedRole;
};
const setRoleAndRedirect = (userRole: string, type: "CORPORATE" | "PRIVATE", roleRedirectUrls?: { role: string, url: string }[]) => {
    localStorage.setItem("customerType", type);
    const roleRedirect = roleRedirectUrls?.find(r => r.role == userRole);
    if (roleRedirect)
        window.location.href = roleRedirect.url;

}
const getCurrentUserRole = (): UserRole | string => {
    const user = getCurrentUser();
    if (!user)
        return "";
    const decodedJwt = jwtDecode<any>(user);
    if (decodedJwt.scopes.some((s: string) => s == UserRole.CompanyAdmin))
        return UserRole.CompanyAdmin;
    return decodedJwt.scopes[decodedJwt.scopes.length - 1] as UserRole;
}
const isUserInRole = (role: string): boolean => {
    const user = getCurrentUser();
    if (!user)
        return false;
    const decodedJwt = jwtDecode<any>(user);
    return decodedJwt.scopes.findIndex((s: string) => s == role) != -1;
}
const getCurrentUserName = (): string => {
    const user = getCurrentUser();
    if (!user)
        return "";
    const decodedJwt = jwtDecode<any>(user);
    const scope = decodedJwt.userId;
    return scope;
}
const isAuthenticated = (): boolean => {
    return localStorage.getItem("token") ? true : false;
}
const isSystemAdminOrOwner = (): boolean => {
    if (isUserInRole(UserRole.SystemAdmin) || isUserInRole(UserRole.SystemOwner))
        return true;
    return false;
}
const isDealCustomerAdmin = (): boolean => {
    return isUserInRole("DEAL_CUSTOMER_ADMIN");
}
const isCompanyAdmin = (): boolean => {
    return isUserInRole("COMPANY_ADMIN");
}
const isDealCustomerUser = (): boolean => {
    return isUserInRole(UserRole.DealCustomerUser);
}
const logout = () => {
    dataLayerHelper.push({
        event: "logout",
        user_id: getCurrentUserName()
    });
    localStorage.removeItem("lastContactInfo");
    localStorage.removeItem("lastSearchObject");
    localStorage.removeItem("activeOrganizationId");
    // A checkout in progress belongs to the session that started it. Left behind, the wizard
    // would resume somebody else's booking, and its step list — decided under the previous
    // session's auth state — would no longer match who is signed in.
    clearBookabusPriceCheckout();
    localStorage.removeItem("token");
};

const getCurrentUser = () => {
    var tokenJson = localStorage.getItem("token");
    if (!tokenJson)
        return null;
    return JSON.parse(tokenJson).token;
};

const isTrainReplacementCustomer = () => {
    const user = getCurrentUser();
    if (!user)
        return false;
    const decodedJwt = jwtDecode<any>(user);
    return decodedJwt.trainReplacementCustomer;
};

const isTrainReplacementAdmin = () => {
    return isTrainReplacementCustomer() && isUserInRole("DEAL_CUSTOMER_ADMIN");
};
const isTrainReplacementFinance = () => {
    return isTrainReplacementCustomer() && isUserInRole("DEAL_CUSTOMER_FINANCE");
};
const isTrafficControlUser = () => {
    return isUserInRole(UserRole.TrafficController);
};


const onAuthStateChanged = (onChange?: any, authState?: boolean) => {
    if (authState)
        onChange(authState);
}
const getPepper = () => {
    return axios.post<null, AxiosResponse<string>>(API_URL + "/auth/pepper")
        .then((response) => {
            localStorage.setItem("pepper", response.data);
            window.location.reload();
        });
}

const authService = {
    registerCompany,
    login,
    logout,
    getCurrentUser,
    registerCustomer,
    getCurrentUserRole,
    getCurrentUserName,
    isAuthenticated,
    onAuthStateChanged,
    validateCustomerEmail,
    changePassword,
    validateChangePasswordHash,
    getPepper,
    isUserInRole,
    isSystemAdminOrOwner,
    isDealCustomerAdmin,
    isDealCustomerUser,
    isCompanyAdmin,
    isTrainReplacementCustomer,
    isTrafficControlUser,
    isTrainReplacementAdmin,
    getMagicLink,
    verifyMagicLink,
    loginWithGoogle,
    handleTokenLogin,
    isTrainReplacementFinance
};
export default authService;